import { SalesforceObjectType } from "@amzn/ads-measurement-plm-service-type-script-client";
import type { SfContext } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { Modal } from "@amzn/storm-ui";
import * as React from "react";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { useStore } from "zustand";

import { getBlinkApiClientV2 } from "../../../../api/blinkApi/blinkApi";
import type { PitchFileUploadActionRequest } from "../../../../hooks/pitchedContent/usePitchFileUploadAction";
import { editContentStore } from "../../../../store/editContent";
import type { SfObject } from "../../../../store/insightLinkage";
import { SfObjectType } from "../../../../store/insightLinkage";
import { InputComponentTypes } from "../../../../types/inputs";
import {
    getAdvertiserId,
    getSfObjectId,
    getSfObjectType,
    QueryParam,
} from "../../../../utils/permission.utils";
import { NOT_CREATED_DROPDOWN_VALUE } from "../../../../utils/salesforce.utils";
import { removeFileExtension } from "../../../../utils/string.utils";
import { InputComponent } from "../../../common/Input";
import { TextAreaComponent } from "../../../common/TextArea";
import { ModalContainer } from "../../../common/styled.components";

import { FileUpload } from "./FileUpload";
import { UploadPitchedContentFooter } from "./UploadPitchedContentFooter";
import { UploadPitchedContentSalesforceSelector } from "./UploadPitchedContentSalesforceSelector";

const UploadModalContainer = styled.div`
    width: 40vw;
    display: flex;
    justify-content: center;
`;

export const UploadPitchedContentModal: React.FunctionComponent = () => {
    const [editContent, isModalOpen, closeEditModal, onSubmit] = useStore(
        editContentStore,
        (state) => [
            state.content,
            state.shouldShowEditModal,
            state.closeEditModal,
            state.onSubmitHandler,
        ],
    );

    const defaultSfObject: SfObject = {
        sfObjectId: "",
        sfObjectType: SfObjectType.OPPORTUNITY,
    };

    const [insightTitle, setInsightTitle] = React.useState<string>("");

    const [sfObject, setSfObject] = React.useState<SfObject>(defaultSfObject);

    const [file, setFile] = React.useState<File>();

    const [fileDisplayName, setFileDisplayName] = React.useState<string>();

    const [notes, setNotes] = React.useState<string>("");

    const [contentSFContext, setContentSFContext] = React.useState<SfContext>();

    const [params, setParams] = useSearchParams();

    const [canEditOpportunity, setCanEditOpportunity] =
        React.useState<boolean>(true);

    const sfObjectType = getSfObjectType();
    const sfObjectId = getSfObjectId();

    const handleClose = () => {
        setInsightTitle("");
        setSfObject(defaultSfObject);
        setFile(undefined);
        setFileDisplayName(undefined);
        setNotes("");
        closeEditModal();
        setCanEditOpportunity(true);
        setContentSFContext(undefined);
        clearSfObjectParams();
    };

    useEffect(() => {
        if (sfObjectType && sfObjectId) {
            setSfObject({
                sfObjectId,
                sfObjectType,
            });
        }
    }, [sfObjectType, sfObjectId]);

    const setUploadFile = (file: File) => {
        setFileDisplayName(undefined);
        setFile(file);

        if (!insightTitle && file.name) {
            setInsightTitle(removeFileExtension(file.name));
        }
    };

    const getOpportunityId = () => {
        if (!canEditOpportunity) {
            return editContent?.accountOpportunities?.[0]?.opportunityId ?? "";
        }

        if (
            sfObject.sfObjectType !== SfObjectType.OPPORTUNITY ||
            sfObject.sfObjectId === NOT_CREATED_DROPDOWN_VALUE
        ) {
            return "";
        }

        return sfObject.sfObjectId;
    };

    const getSalesforceAccount = () => {
        const salesforceAccountId =
            editContent?.associations?.[0]?.salesforceAssociation
                ?.salesforceAccountId;
        if (salesforceAccountId) {
            return salesforceAccountId;
        }

        const accountOpportunityId =
            editContent?.accountOpportunities?.[0]?.accountId;
        if (accountOpportunityId) {
            return accountOpportunityId;
        }

        return getAdvertiserId();
    };

    const loadContentSFContext = async () => {
        const { data } = await (
            await getBlinkApiClientV2()
        ).getSfContext({
            advertiserId: getSalesforceAccount(),
        });
        setContentSFContext(data);
    };

    const getUploadProps: () => PitchFileUploadActionRequest = () => {
        const shared = {
            insightTitle,
            sfAccountId: getSalesforceAccount()!,
            sfObject,
            accountOpportunityId: `${getSalesforceAccount()}_${getOpportunityId()}`,
            notes,
        };
        return editContent
            ? { ...shared, contentId: editContent.contentId!, file }
            : { ...shared, file: file! };
    };

    const clearSfObjectParams = () => {
        const newSearchParams = new URLSearchParams(params);
        newSearchParams.delete(QueryParam.SALESFORCE_OBJECT_TYPE);
        newSearchParams.delete(QueryParam.SALESFORCE_OBJECT_ID);
        setParams(newSearchParams);
    };

    React.useEffect(() => {
        if (isModalOpen && editContent) {
            setInsightTitle(editContent.contentTitle ?? "");
            setFile(undefined);
            setFileDisplayName(editContent.fileName);
            setNotes(editContent?.contentNote ?? "");

            const sfAssociation =
                editContent.associations[0]?.salesforceAssociation;
            const defaultOpportunity = editContent.accountOpportunities?.[0];

            if (
                sfAssociation?.salesforceObjectId &&
                sfAssociation.salesforceObjectType !==
                    SalesforceObjectType.ACCOUNT_ONLY
            ) {
                setSfObject({
                    sfObjectId: sfAssociation.salesforceObjectId,
                    sfObjectType:
                        sfAssociation.salesforceObjectType as SfObjectType,
                });
            } else if (defaultOpportunity?.opportunityId) {
                setSfObject({
                    sfObjectId: defaultOpportunity.opportunityId,
                    sfObjectType: SfObjectType.OPPORTUNITY,
                });
            } else {
                setSfObject({
                    sfObjectId: NOT_CREATED_DROPDOWN_VALUE,
                    sfObjectType: SfObjectType.ACTIVITY,
                });
            }

            setCanEditOpportunity(getSalesforceAccount() === getAdvertiserId());
        }
        if (isModalOpen) {
            void loadContentSFContext();
        }
    }, [isModalOpen, editContent]);

    return (
        <Modal
            id="upload-pitched-content-modal"
            data-testid="upload-pitched-content-modal"
            isOpen={isModalOpen}
            header={`${editContent ? "Edit" : "Upload"} insight deck`}
            onClose={handleClose}
            padding="xsmall"
            style={{ zIndex: "800" }}
            footer={
                <UploadPitchedContentFooter
                    uploadProps={getUploadProps()}
                    handleClose={handleClose}
                    onSubmit={onSubmit}
                />
            }
        >
            <UploadModalContainer>
                <ModalContainer style={{ width: "40vw" }}>
                    <InputComponent
                        label="Insight deck title"
                        type={InputComponentTypes.TEXT}
                        dimension="insight-deck-title"
                        value={insightTitle}
                        setValue={setInsightTitle}
                    />
                    <UploadPitchedContentSalesforceSelector
                        editContent={editContent}
                        sfAccountId={getSalesforceAccount()}
                        sfObject={sfObject}
                        setSfObject={setSfObject}
                        contentSfContext={contentSFContext}
                        closeModal={handleClose}
                    />
                    <FileUpload
                        file={file}
                        setFile={setUploadFile}
                        displayName={fileDisplayName}
                    />
                    <TextAreaComponent
                        label="Notes"
                        dimension="notes"
                        value={notes}
                        setValue={setNotes}
                        optional
                    />
                </ModalContainer>
            </UploadModalContainer>
        </Modal>
    );
};
